import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Typography,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { CloudUpload, AttachFile } from "@material-ui/icons";
import notice from "../../utils/notice";

// TZ-789 admin: single upload "zone" for one xlsx file. The page mounts
// two of these — one for staff, one for students. Each is independent:
// admin can upload one without the other, in any order, on different
// days. Selection is local state; the parent owns the result.

const UploadZone = ({ title, description, accept, onUpload, disabled }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Single source for the input id so the <input> id and the <label
  // htmlFor> stay in sync if the slug format ever changes.
  const inputId = `upload_input_${title.replace(/\s+/g, "_").toLowerCase()}`;

  const handlePick = (e) => {
    const next = e.target.files && e.target.files[0];
    if (!next) return;
    // Client-side extension guard — the input's `accept` attribute
    // already filters the file dialog by default, but users can still
    // pick "All files" and choose something else. Server rejects with
    // a 400 in that case; surfacing the same problem inline avoids
    // the round trip. Browser-reported file.type is unreliable for
    // xlsx (some browsers leave it blank), so we trust the extension.
    if (!next.name.toLowerCase().endsWith(".xlsx")) {
      notice("Please choose an .xlsx file", "error");
      // Treat the new (invalid) pick as superseding any previous one
      // — otherwise the display would still show the prior valid
      // file name while the native input holds something else, which
      // is confusing. Clears both React state and the DOM input.
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setFile(next);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } catch (error) {
      // The CRUD layer already surfaced the failure via notice() —
      // catching here just stops the rejection from bubbling to the
      // click handler as an unhandled promise. Console line keeps the
      // stack trace available in dev for debugging.
      console.error("Upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <Paper style={{ padding: 24, height: "100%" }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {description}
      </Typography>

      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handlePick}
          style={{ display: "none" }}
          id={inputId}
        />
        <label htmlFor={inputId}>
          <Button
            variant="outlined"
            component="span"
            startIcon={<AttachFile />}
            disabled={disabled || uploading}
          >
            {file ? "Choose another file" : "Choose file"}
          </Button>
        </label>
        {file && (
          <Typography
            variant="body2"
            style={{ marginTop: 8, wordBreak: "break-all" }}
          >
            <strong>Selected:</strong> {file.name}
          </Typography>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            uploading ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <CloudUpload />
            )
          }
          disabled={!file || disabled || uploading}
          onClick={handleUpload}
        >
          {uploading ? "Uploading…" : "Upload"}
        </Button>
        {file && !uploading && (
          <Button onClick={handleReset} disabled={disabled}>
            Clear
          </Button>
        )}
      </div>
    </Paper>
  );
};

UploadZone.propTypes = {
  title:       PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  accept:      PropTypes.string,
  onUpload:    PropTypes.func.isRequired,
  disabled:    PropTypes.bool,
};

UploadZone.defaultProps = {
  accept:   ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  disabled: false,
};

export default UploadZone;
